import { initializeApp } from 'firebase/app';
import {
    getFirestore,
    collection,
    query,
    where,
    getDocs
} from 'firebase/firestore';
import type { Pergunta, Desafio, Conquista } from '../types/quiz.types';

// Bundled local JSON fallback for resilient offline / slow connection gameplay
const localFasesModules = import.meta.glob('../../upload/db/fases_raw/fase_*.json', { eager: true });
const localDesafiosModule = import.meta.glob('../../upload/db/desafios_all.json', { eager: true });
const localItensTendaModule = import.meta.glob('../../upload/db/itens_tenda.json', { eager: true });
const localConquistasModule = import.meta.glob('../../upload/db/conquistas.json', { eager: true });

function getLocalPerguntasByFase(fase: number): Pergunta[] {
    const pad = String(fase).padStart(2, '0');
    const key = `../../upload/db/fases_raw/fase_${pad}.json`;
    const mod = localFasesModules[key] as any;
    if (!mod) return [];
    const list: Pergunta[] = Array.isArray(mod) ? mod : mod.default || [];
    return list.filter(p => p.status === 'ativo' || !p.status);
}

function getLocalDesafios(): Desafio[] {
    const key = Object.keys(localDesafiosModule)[0];
    if (!key) return [];
    const mod = localDesafiosModule[key] as any;
    return Array.isArray(mod) ? mod : mod.default || [];
}

/**
 * Um desafio serve a uma fase se `fases_validas` a contiver ou se ela cair dentro
 * de `range_fase`. Os dois formatos coexistem: o catálogo gerado usa `range_fase`
 * e o deploy do Firestore expande para `fases_validas`.
 */
export function desafioServeFase(desafio: Desafio, fase: number): boolean {
    if (Array.isArray(desafio.fases_validas)) {
        return desafio.fases_validas.includes(fase);
    }
    const range = desafio.range_fase;
    if (Array.isArray(range) && range.length === 2) {
        return fase >= Number(range[0]) && fase <= Number(range[1]);
    }
    return false;
}

function getLocalItensTenda() {
    const key = Object.keys(localItensTendaModule)[0];
    if (!key) return [];
    const mod = localItensTendaModule[key] as any;
    return Array.isArray(mod) ? mod : mod.default || [];
}

function getLocalConquistas(): Conquista[] {
    const key = Object.keys(localConquistasModule)[0];
    if (!key) return [];
    const mod = localConquistasModule[key] as any;
    return Array.isArray(mod) ? mod : mod.default || [];
}

const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY?.trim(),
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN?.trim(),
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID?.trim(),
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET?.trim(),
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID?.trim(),
    appId: import.meta.env.VITE_FIREBASE_APP_ID?.trim()
};

const app = initializeApp(firebaseConfig);
const rawDbId = import.meta.env.VITE_FIREBASE_DATABASE_ID || "quiz-db";
const dbId = rawDbId.trim();
export const db = getFirestore(app, dbId);

/**
 * Busca todas as perguntas de uma Fase com timeout e fallback local para garantir resiliência 100%.
 */
export async function fetchPerguntasByFase(fase: number): Promise<Pergunta[]> {
    const faseNum = Number(fase);

    const firestoreQueryPromise = (async (): Promise<Pergunta[]> => {
        const q = query(
            collection(db, 'perguntas'),
            where('fase', '==', faseNum),
            where('status', '==', 'ativo')
        );
        const snapshot = await getDocs(q);
        if (snapshot.empty) return [];
        const result: Pergunta[] = [];
        snapshot.forEach(docSnap => {
            result.push({
                id: docSnap.id,
                ...docSnap.data()
            } as Pergunta);
        });
        return result;
    })();

    const timeoutPromise = new Promise<Pergunta[]>((resolve) =>
        setTimeout(() => resolve([]), 2500)
    );

    try {
        const perguntas = await Promise.race([firestoreQueryPromise, timeoutPromise]);
        if (perguntas && perguntas.length > 0) {
            return perguntas;
        }
    } catch (err) {
        console.warn(`[quizFirebaseService] Conexão remota com Firestore oscilou para fase ${faseNum}. Utilizando dados sincronizados:`, err);
    }

    // Fallback local garantido
    const local = getLocalPerguntasByFase(faseNum);
    if (local.length > 0) {
        return local;
    }

    throw new Error(`Nenhuma pergunta encontrada para a Fase ${fase}.`);
}

/**
 * Busca desafios com fallback resiliente.
 */
export async function fetchDesafioAleatorio(fase: number, excludeIds: string[]): Promise<Desafio | null> {
    const faseNum = Number(fase);

    const firestorePromise = (async (): Promise<Desafio | null> => {
        const q = query(
            collection(db, 'desafios'),
            where('fases_validas', 'array-contains', faseNum)
        );
        const snapshot = await getDocs(q);
        if (snapshot.empty) return null;
        const disponiveis: Desafio[] = [];
        snapshot.forEach(docSnap => {
            if (!excludeIds.includes(docSnap.id)) {
                disponiveis.push({ id: docSnap.id, ...docSnap.data() } as Desafio);
            }
        });
        if (disponiveis.length === 0) return null;
        return disponiveis[Math.floor(Math.random() * disponiveis.length)];
    })();

    const timeoutPromise = new Promise<Desafio | null>((resolve) =>
        setTimeout(() => resolve(null), 2500)
    );

    try {
        const desafio = await Promise.race([firestorePromise, timeoutPromise]);
        if (desafio) return desafio;
    } catch (e) {
        // ignora
    }

    // Fallback local
    const todos = getLocalDesafios();
    const disponiveis = todos.filter(d => desafioServeFase(d, faseNum) && !excludeIds.includes(d.id));

    if (disponiveis.length === 0) return null;
    return disponiveis[Math.floor(Math.random() * disponiveis.length)];
}

/**
 * Busca itens da Tenda com fallback resiliente.
 */
export async function fetchItensTenda() {
    try {
        const firestorePromise = (async () => {
            const snapshot = await getDocs(collection(db, 'itens_tenda'));
            if (snapshot.empty) return [];
            const itens: any[] = [];
            snapshot.forEach(docSnap => itens.push(docSnap.data()));
            return itens;
        })();

        const timeoutPromise = new Promise<any[]>((resolve) => setTimeout(() => resolve([]), 2500));
        const res = await Promise.race([firestorePromise, timeoutPromise]);
        if (res && res.length > 0) return res;
    } catch (e) {
        // ignora
    }

    return getLocalItensTenda();
}

export async function fetchConquistasGlobais(): Promise<Conquista[]> {
    try {
        const firestorePromise = (async () => {
            const snapshot = await getDocs(collection(db, 'conquistas'));
            if (snapshot.empty) return [];
            const conquistas: Conquista[] = [];
            snapshot.forEach(docSnap => conquistas.push(docSnap.data() as Conquista));
            return conquistas;
        })();

        const timeoutPromise = new Promise<Conquista[]>((resolve) => setTimeout(() => resolve([]), 2500));
        const res = await Promise.race([firestorePromise, timeoutPromise]);
        if (res && res.length > 0) return res;
    } catch (e) {
        // ignora
    }

    return getLocalConquistas();
}
